import React from 'react';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { DayPicker } from '@lifeomic/chroma-react/components/DayPicker';
import { makeStyles } from '@lifeomic/chroma-react/styles';

import { defineMessages, useIntl } from 'react-intl';
import { ExecutiveSummaryData } from './executive-summary';
import { useDurationStrings } from './useDurationStrings';

const useStyles = makeStyles(_theme => ({
  dayPicker: {
    '& > div, & input': {
      minWidth: 'unset',
    },
  },
}));

const messages = defineMessages({
  planDuration: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.planDuration',
    defaultMessage: 'Effective Dates',
  },
  planDurationStart: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.planDurationStart',
    defaultMessage: 'Start',
  },
  planDurationEnd: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.planDurationEnd',
    defaultMessage: 'End',
  },
  error: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.error',
    defaultMessage: 'Start date must be before end date',
  },
  dateRange: {
    id: 'phc-web-toolkit.subjectViewer.actionPlan.dateRange',
    defaultMessage: '{start} - {end}',
  },
});

const DAY_MS = 1000 * 60 * 60 * 24;
const thirtyDaysFromNow = () => new Date(Date.now() + 30 * DAY_MS);

export const Duration = ({
  executiveSummary,
  onDurationChange,
  disabled,
  isEditing,
}: {
  executiveSummary: ExecutiveSummaryData;
  onDurationChange: (dates: { start?: Date; end?: Date }) => void;
  disabled?: boolean;
  isEditing: boolean;
}) => {
  const { formatMessage } = useIntl();
  const [error, setError] = React.useState<string | null>(null);
  const classes = useStyles({});

  const { start, end } = useDurationStrings(executiveSummary.carePlan);

  const handleUpdate = ({ start, end }: { start?: Date; end?: Date }) => {
    if (start && end && start > end) {
      setError(formatMessage(messages.error));
      return;
    }
    setError(null);
    onDurationChange({ start, end });
  };

  return (
    <Box direction="column" gap={1}>
      <Text size="subbody" weight="bold">
        {formatMessage(messages.planDuration)}
      </Text>
      <Box direction="row" gap={2}>
        {isEditing ? (
          <>
            <DayPicker
              aria-label={formatMessage(messages.planDurationStart)}
              className={classes.dayPicker}
              onDayChange={start =>
                handleUpdate({
                  start,
                  end: executiveSummary.getDuration().end,
                })
              }
              value={executiveSummary.getDuration()?.start || new Date()}
              fullWidth
              disabled={disabled}
            />
            <DayPicker
              aria-label={formatMessage(messages.planDurationEnd)}
              className={classes.dayPicker}
              onDayChange={end =>
                handleUpdate({
                  start: executiveSummary.getDuration().start,
                  end,
                })
              }
              value={
                executiveSummary.getDuration().end ||
                // Arbitrary date in the future
                thirtyDaysFromNow()
              }
              fullWidth
              disabled={disabled}
              anchorPosition="bottom-right"
            />
          </>
        ) : (
          <Text size="subbody">
            {formatMessage(messages.dateRange, {
              start,
              end,
            })}
          </Text>
        )}
      </Box>
      {error && <Text color="negative">{error}</Text>}
    </Box>
  );
};
